import React from 'react';
import {RouteObject, useRoutes} from 'react-router';
import {getPath} from '@epic-core/common';
import {getLocalePath} from 'epic-ue-locale-mode';
import Ue5LandingPage from '../pages/stardust/Ue5LandingPage';
import GeneralLandingStardustPage from '../pages/stardust/GeneralLandingStardustPage';
import EducatorsStardustPage from '../pages/stardust/EducatorsStardustPage';
import FAQStardustPage from '../pages/stardust/FAQStardustPage';
import OnlineLearningStardustPage from '../pages/stardust/OnlineLearningStardustPage';
import PartnerApplyStardustPage from '../pages/stardust/PartnerApplyStardustPage';
import ReleaseNotesStardustPage from '../pages/stardust/ReleaseNotesStardustPage';
import PaidSearchLandingPage from '../pages/stardust/PaidSearchLandingPage';
import {AppSharedKeys, getSharedContext, RouteMatchOrUrlParse} from 'epic-ue-shared';
import URLParse from 'url-parse';
import {InitSharedValueResponse} from '../shared';
import {getAppRedirects} from './appRedirects';
import {getClassicRoutes} from './classicRoutes';
import MegagrantsStardustPage from '../pages/stardust/MegagrantsStardustPage';
import {Redirect, RedirectProps} from './Redirect';
import ExplainersPage from '../pages/stardust/ExplainersStardustPage';
import ExplainersTopicPage from '../pages/stardust/ExplainersTopicStardustPage';
import ExplainersDetailPage from '../pages/stardust/ExplainersDetailStardustPage';
import {TechBlogPage, DeveloperInterviewPage, SpotlightPage} from '../pages/FeedDetailsPage';
import BlogPage from '../pages/BlogPage';
import EventPageContainer from '../pages/EventPage.container';
import NewsletterPage from '../pages/stardust/NewsletterPage';
import HubLandingPage from '../pages/stardust/HubLandingStardustPage';
import CreatorSalePage from '../pages/stardust/CreatorSalePage';
import CmsPage from '../CmsPage';

export type GetInitialSharedValues = (
    match: RouteMatchOrUrlParse,
    url?: URLParse<{[key: string]: string | undefined}>
) =>
    | Promise<InitSharedValueResponse[]>
    | ((match: RouteMatchOrUrlParse) => Promise<InitSharedValueResponse[]>)
    | (() => Promise<InitSharedValueResponse[]>);

export interface ServerRoute {
    getInitialSharedValues?: GetInitialSharedValues;
    path?: string;
    element?: React.ReactNode | null;
}

export interface ConnectedPage {
    (args?: any): JSX.Element;
    getInitialSharedValues?: GetInitialSharedValues;
}

const getStardustPages = (): ServerRoute[] => {
    const context = getSharedContext();
    if (!context) return [];
    const miscStore = context.get(AppSharedKeys.MiscStore);
    const stardustPages = miscStore?.stardustPages || [];
    const routes: ServerRoute[] = [];
    const localePath = getLocalePath();

    stardustPages.forEach((page) => {
        if (!page) {
            return;
        }

        let getInitialSharedValues = GeneralLandingStardustPage.getInitialSharedValues;
        let element = <GeneralLandingStardustPage />;
        if (page.template === 'MEGAGRANTS') {
            element = <MegagrantsStardustPage />;
            getInitialSharedValues = MegagrantsStardustPage.getInitialSharedValues;
        } else if (page.template === 'HUB_LANDING') {
            element = <HubLandingPage />;
            getInitialSharedValues = HubLandingPage.getInitialSharedValues;
        }

        routes.push({
            path: `${localePath}${page.urlPattern}`,
            element,
            getInitialSharedValues
        });
    });

    return routes;
};

const getCosmosRedirects = (): ServerRoute[] => {
    const context = getSharedContext();
    if (!context) return [];

    let routes: ServerRoute[] = [];

    const localePath = getLocalePath();

    routes = [
        {
            path: `${localePath}/educators`,
            element: <Redirect to="/students-and-schools" status={301} />
        },
        {
            path: `${localePath}/students`,
            element: <Redirect to="/students-and-schools" status={301} />
        },
        {
            path: `${localePath}/uefn-unreal-editor-for-fortnite`,
            element: <Redirect to="/uses/uefn-unreal-editor-for-fortnite" status={301} />
        },
        {
            path: `${localePath}/learn`,
            element: (
                <Redirect
                    url="https://dev.epicgames.com/community/unreal-engine/learning"
                    status={301}
                />
            )
        }
    ];

    return routes;
};

const getStardustRedirects = (): ServerRoute[] => {
    const context = getSharedContext();
    if (!context) return [];
    const miscStore = context.get(AppSharedKeys.MiscStore);
    const stardustRedirects = miscStore?.stardustRedirects || [];
    const routes: ServerRoute[] = [];
    const localePath = getLocalePath();

    stardustRedirects.forEach((page) => {
        if (!page) {
            return;
        }

        let from = page.from || '';
        //prefix a slash if not found
        if (!/^\//.test(from)) {
            from = `/${from}`;
        }

        const to = page.to || '';
        const url = page.url || '';
        const permanent = page.permanent;

        if (!from || (!to && !url)) {
            return;
        }

        const props: RedirectProps = {};
        if (to) {
            props.to = to;
        }
        if (url) {
            props.url = url;
        }

        routes.push({
            path: `${localePath}${page.from}`,
            element: <Redirect {...props} status={permanent ? 301 : 302} replace={props.replace} />
        });
    });

    return routes;
};

const getFirstPriorityRoutes = () => {
    const redirects = getAppRedirects();
    const stardustRedirects = getStardustRedirects();
    const stardustPages = getStardustPages();
    const cosmosRedirects = getCosmosRedirects();

    const allRedirects = redirects.concat(stardustRedirects).concat(cosmosRedirects);
    return allRedirects.concat(stardustPages);
};

/**
 * All app routes are defined here and have an additional property for determining
 * what data to load on the server and init into shared values
 */
export const getServerRoutes = (): ServerRoute[] => {
    const path = getPath();
    const localePath = getLocalePath();

    const classicRoutes = getClassicRoutes();
    const firstRoutes = getFirstPriorityRoutes();

    const stardustRoutes = firstRoutes.concat([
        {
            path: `${localePath}/project-anywhere`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/ue-dynamic-qatest`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/ue-on-github`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/unreal-engine-5`,
            getInitialSharedValues: Ue5LandingPage.getInitialSharedValues,
            element: <Ue5LandingPage />
        },
        {
            path: `${localePath}/unreal-futures`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/digital-humans`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/connectors`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/nextgen`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/test-multicard-video-do-not-publish`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/fellowship`,
            getInitialSharedValues: MegagrantsStardustPage.getInitialSharedValues,
            element: <MegagrantsStardustPage />
        },
        {
            path: `${localePath}/animation-field-guide`,
            getInitialSharedValues: MegagrantsStardustPage.getInitialSharedValues,
            element: <MegagrantsStardustPage />
        },
        // TODO: Hardcoded locale routes — update for locale mode "never" support
        {
            path: `${path}ja/onlinelearning-courses`,
            getInitialSharedValues: OnlineLearningStardustPage.getInitialSharedValues,
            element: <OnlineLearningStardustPage />
        },
        {
            path: `${path}ko/onlinelearning-courses`,
            getInitialSharedValues: OnlineLearningStardustPage.getInitialSharedValues,
            element: <OnlineLearningStardustPage />
        },
        {
            path: `${path}zh-CN/onlinelearning-courses`,
            getInitialSharedValues: OnlineLearningStardustPage.getInitialSharedValues,
            element: <OnlineLearningStardustPage />
        },
        {
            path: `${localePath}/virtual-production`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/creators`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/training-partners-apply`,
            getInitialSharedValues: PartnerApplyStardustPage.getInitialSharedValues,
            element: <PartnerApplyStardustPage />
        },
        {
            path: `${localePath}/academic-partners-apply`,
            getInitialSharedValues: MegagrantsStardustPage.getInitialSharedValues,
            element: <MegagrantsStardustPage />
        },
        {
            path: `${localePath}/authorized-instructor-apply`,
            getInitialSharedValues: PartnerApplyStardustPage.getInitialSharedValues,
            element: <PartnerApplyStardustPage />
        },
        {
            path: `${localePath}/try-free/:slug`,
            getInitialSharedValues: PaidSearchLandingPage.getInitialSharedValues,
            element: <PaidSearchLandingPage />
        },
        {
            path: `${localePath}/updates`,
            getInitialSharedValues: ReleaseNotesStardustPage.getInitialSharedValues,
            element: <ReleaseNotesStardustPage />
        },
        {
            path: `${localePath}/updates/:slug`,
            getInitialSharedValues: ReleaseNotesStardustPage.getInitialSharedValues,
            element: <ReleaseNotesStardustPage />
        },
        {
            path: `${localePath}/faq`,
            getInitialSharedValues: FAQStardustPage.getInitialSharedValues,
            element: <FAQStardustPage />
        },
        {
            path: `${localePath}/community`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/unreal-indies`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/educators/accelerator`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/educators`,
            getInitialSharedValues: EducatorsStardustPage.getInitialSharedValues,
            element: <EducatorsStardustPage />
        },
        {
            path: `${localePath}/eula/:eulaDesc`,
            getInitialSharedValues: CmsPage.getInitialSharedValues,
            element: <CmsPage />
        },
        {
            path: `${localePath}/explainers`,
            getInitialSharedValues: ExplainersPage.getInitialSharedValues,
            element: <ExplainersPage />
        },
        {
            path: `${localePath}/explainers/:topic`,
            getInitialSharedValues: ExplainersTopicPage.getInitialSharedValues,
            element: <ExplainersTopicPage />
        },
        {
            path: `${localePath}/explainers/:topic/:slug`,
            getInitialSharedValues: ExplainersDetailPage.getInitialSharedValues,
            element: <ExplainersDetailPage />
        },
        {
            path: `${localePath}/blog/:title`,
            getInitialSharedValues: BlogPage.getInitialSharedValues,
            element: <BlogPage />
        },
        {
            path: `${localePath}/spotlights/:slug`,
            getInitialSharedValues: SpotlightPage.getInitialSharedValues,
            element: <SpotlightPage rootPageSlug="spotlights" />
        },
        {
            path: `${localePath}/developer-interviews/:slug`,
            getInitialSharedValues: DeveloperInterviewPage.getInitialSharedValues,
            element: <DeveloperInterviewPage rootPageSlug="developer-interviews" />
        },
        {
            path: `${localePath}/tech-blog/:slug`,
            getInitialSharedValues: TechBlogPage.getInitialSharedValues,
            element: <TechBlogPage rootPageSlug="tech-blog" />
        },
        {
            path: `${localePath}/events/:title`,
            getInitialSharedValues: EventPageContainer.getInitialSharedValues,
            element: <EventPageContainer />
        },
        {
            path: `${localePath}/newsletter`,
            getInitialSharedValues: NewsletterPage.getInitialSharedValues,
            element: <NewsletterPage />
        },
        {
            path: `${localePath}/springcreatorsale`,
            getInitialSharedValues: CreatorSalePage.getInitialSharedValues,
            element: <CreatorSalePage />
        },
        {
            path: `${localePath}/qa-test/faq`,
            getInitialSharedValues: FAQStardustPage.getInitialSharedValues,
            element: <FAQStardustPage />
        },
        {
            path: `${localePath}/qa-test/blog/:title`,
            getInitialSharedValues: BlogPage.getInitialSharedValues,
            element: <BlogPage />
        },
        {
            path: `${localePath}/qa-test/general-landing`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        },
        {
            path: `${localePath}/qa-test/general-landing/:title`,
            getInitialSharedValues: GeneralLandingStardustPage.getInitialSharedValues,
            element: <GeneralLandingStardustPage />
        }
    ]);

    const serverRoutes = stardustRoutes.concat(classicRoutes);
    return serverRoutes;
};

/**
 * Returns routes in an array that react-router expects, dropping the extra fields used for the server render
 */
export const getRoutes = (): RouteObject[] => {
    const serverRoutes = getServerRoutes();
    const routes = serverRoutes.map((route) => {
        return {
            path: route.path,
            element: route.element
        };
    });

    return routes;
};

export const useRoutesElement = (): React.ReactElement | null => {
    return useRoutes(getRoutes());
};

export default getRoutes;
